<template>
  <div class="exam-page">
    <div class="exam-card">
      <div class="card-header">
        <h2 class="card-title">在线考试</h2>
      </div>
      <div v-if="detailInfo.exam && detailInfo.exam.length > 0">
        <div
          v-for="(item, index) in detailInfo.exam"
          :key="index"
          class="exam-info-card-item"
        >
          <div class="exam-info-card-title">
            {{ item.examTitle }}
          </div>
          <div v-if="formatExamTimeRange(item)" class="exam-time-range">
            考试时间：{{ formatExamTimeRange(item) }}
          </div>
          <div
            class="exam-info-card-content"
            v-if="item.histories && item.histories.length > 0"
          >
            <div
              class="exam-info-card"
              v-for="(hitem, index) in item.histories"
              :key="index"
            >
              <div class="exam-content">
                <div class="exam-name">
                  {{ hitem.title }}
                </div>

                <div
                  v-if="!item?.originExam?.needCam"
                  class="score-badge"
                  :class="{ 'score-badge-pass': hitem.score === 100 }"
                >
                  {{ hitem.score }}
                </div>
              </div>

              <div
                class="wrong-questions-link"
                v-if="hitem.score !== 100 && !item?.originExam?.needCam"
              >
                <a
                  href="#"
                  @click.prevent="viewWrongQuestions(hitem)"
                  class="link-text"
                >
                  查看错题
                </a>
              </div>
            </div>
          </div>
          <div v-else>
            <div class="exam-info-card-text" style="margin-bottom: 20px">
              您还没有参加过此考试
            </div>
          </div>
          <!-- 禁用 
             考试完成
             考试结束
             考试次数为0 -->
          <div class="exam-button-section">
            <button
              class="exam-button"
              @click="goToExam(item)"
              :class="{ 'exam-button-disabled': isDisabled(item) }"
            >
              {{ getButtonText(item) }}
            </button>
          </div>
        </div>
      </div>
      <div v-else>
        <div class="exam-info-card-text">暂无考试</div>
      </div>
    </div>
  </div>
</template>

<script setup>
import moment from "moment";
import { Toast } from "vant";
import { useRouter, useRoute } from "vue-router";
import { get } from "@/request.js";
import {
  getExamButtonText,
  hasPendingQuestionnaire,
  isExamActionDisabled,
} from "@/utils/pendingQuestionnaireFlow.cjs";

const router = useRouter();
const route = useRoute();
const props = defineProps({
  detailInfo: {
    type: Object,
    default: () => ({}),
  },
});

// 获取课程详情ID（registrationId），优先从路由参数获取，其次从 detailInfo.id 获取
const getRegistrationId = () => {
  return route.query?.id || props.detailInfo?.id || "";
};

// 格式化考试时间段，与 PC 端一致：YYYY-MM-DD HH:mm ~ YYYY-MM-DD HH:mm
const formatExamTimeRange = (examItem) => {
  const origin = examItem?.originExam;
  if (!origin?.startDate || !origin?.endDate) return "";
  const startDateStr = moment(origin.startDate).format("YYYY-MM-DD");
  const endDateStr = moment(origin.endDate).format("YYYY-MM-DD");
  const startTimeStr = origin.startTime || "00:00";
  const endTimeStr = origin.endTime || "23:59";
  return `${startDateStr} ${startTimeStr}~${endDateStr} ${endTimeStr}`;
};

const goToExam = async (examItem) => {
  // if(examItem.maxRetryTimes==1){
  //   Toast("当前考试不支持使用手机考试");
  //   return;
  // }
  if (isDisabled(examItem)) {
    return;
  }

  // 检查必要的数据是否存在
  if (!examItem?.originExam) {
    Toast("考试信息不完整");
    return;
  }

  // 补填已经触发的问卷不属于再次考试，不受头像、摄像头和考试期限制。
  if (hasPendingQuestionnaire(examItem)) {
    try {
      const res = await get("/api/my/exam/list");
      if (res.success) {
        const updatedItem = res.data.find(
          (el) => el.examinationId === examItem.examId
        );
        if (updatedItem && !updatedItem.hasPendingQuestionnaire) {
          Toast("问卷已完成，无法再次进入问卷页面");
          if (
            props.detailInfo &&
            typeof props.detailInfo.refresh === "function"
          ) {
            props.detailInfo.refresh();
          }
          return;
        }
      }
    } catch (error) {
      console.error("检查问卷状态失败:", error);
      // 后端 startExam 仍会执行最终校验。
    }

    router.push({
      path: "/mobileExamination",
      query: {
        title: examItem.examTitle || "",
        examinationId: examItem.examId,
        registrationId: getRegistrationId(),
      },
    });
    return;
  }

  // 检查证书是否需要用户头像
  const certificateDetails = examItem?.originExam?.certificateDetails;
  if (certificateDetails?.field?.avatar) {
    // 证书需要用户头像，检查用户是否已上传头像
    const userAvatar = props.detailInfo?.candidaterProfile?.avatar;
    if (!userAvatar) {
      Toast("该场考试的证书需要使用用户头像，请先上传头像");
      // 1.5秒后跳转到用户信息页面
      setTimeout(() => {
        router.push({
          path: "/userInfo",
        });
      }, 1500);
      return;
    }
  }

  const originExam = examItem.originExam;

  // 计算开始时间：startDate（时间戳） + startTime（时分）
  const startDateObj = new Date(originExam.startDate);
  if (originExam.startTime) {
    const [startHour, startMinute] = originExam.startTime
      .split(":")
      .map(Number);
    if (!isNaN(startHour) && !isNaN(startMinute)) {
      startDateObj.setHours(startHour, startMinute, 0, 0);
    }
  }
  const startTimestamp = startDateObj.getTime();

  // 计算结束时间：endDate（时间戳） + endTime（时分）
  const endDateObj = new Date(originExam.endDate);
  if (originExam.endTime) {
    const [endHour, endMinute] = originExam.endTime.split(":").map(Number);
    if (!isNaN(endHour) && !isNaN(endMinute)) {
      endDateObj.setHours(endHour, endMinute, 59, 999); // 设置为当天的最后一秒
    }
  }
  const endTimestamp = endDateObj.getTime();

  const currentTimestamp = new Date().getTime();

  // 获取课程详情ID
  const registrationId = getRegistrationId();

  // 检查剩余考试次数（在时间检查之前）
  if (examItem.maxRetryTimes !== undefined && examItem.maxRetryTimes <= 0) {
    const alreadyPassed =
      examItem.originExam?.certPass === true || examItem.examPass;
    let message = alreadyPassed
      ? "该考试已及格，无法再次考试"
      : "考试次数已用完";
    Toast(message);
    return;
  }

  if (currentTimestamp >= startTimestamp && currentTimestamp <= endTimestamp) {
    router.push({
      path: "/mobileExamination",
      query: {
        title: examItem.examTitle || "",
        examinationId: examItem.examId,
        registrationId: registrationId, // 传递课程详情ID，用于返回
      },
    });
  } else if (currentTimestamp < startTimestamp) {
    Toast("考试未开始");
  } else {
    Toast("考试已结束");
  }
};
const isDisabled = (examItem) => {
  return isExamActionDisabled(examItem);
};

const getButtonText = (examItem) => {
  return getExamButtonText(examItem);
};

const viewWrongQuestions = (val) => {
  // 获取课程详情ID
  const registrationId = getRegistrationId();
  router.push({
    path: "/wrongQuestion",
    query: {
      examHistoryId: val.examHistoryId,
      parentId: val.parentId,
      registrationId: registrationId, // 传递课程详情ID，用于返回
    },
  });
};
</script>

<style scoped lang="less">
.exam-page {
  background-color: #f8f8f8;
}

.exam-card {
  background: #ffffff;
  border-radius: 12px;
  padding: 24px 10px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
}

.card-header {
  margin-bottom: 10px;
  border-bottom: 1px solid #f3f4f6;
  padding-bottom: 16px;

  .card-title {
    font-size: 18px;
    font-weight: 600;
    color: #1f2937;
    margin: 0;
    text-align: left;
  }
}
.exam-info-card-item {
  margin-bottom: 10px;
  padding: 15px 10px;
  border-bottom: 15px solid #f8f8f8;
  &:last-child {
    border-bottom: none;
  }
  .exam-info-card-title {
    font-size: 16px;
    font-weight: 600;
    color: #1f2937;
    padding: 20px 0;
  }
  .exam-time-range {
    font-size: 14px;
    color: #6b7280;
    margin-bottom: 12px;
    line-height: 1.5;
  }
}

.exam-info-card-text {
  text-align: center;
  padding: 40px 0;
  font-size: 16px;
  color: #666666;
}
.exam-info-card {
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 30px;
  position: relative;
  min-height: 80px;

  .exam-content {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 12px;

    .exam-name {
      font-size: 16px;
      font-weight: 500;
      color: #374151;
      flex: 1;
      word-wrap: break-word;
      word-break: break-word;
      line-height: 1.4;
    }

    .score-badge {
      width: 40px;
      height: 40px;
      background: #ef4444;
      color: white;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 16px;
      font-weight: 600;
      margin-left: 16px;
    }
    .score-badge-pass {
      background: #10b981;
    }
  }

  .wrong-questions-link {
    display: flex;
    justify-content: flex-end;
    margin-top: 12px;

    .link-text {
      color: #ef4444;
      font-size: 14px;
      text-decoration: none;
      font-weight: 500;

      &:hover {
        text-decoration: underline;
      }
    }
  }
}

.exam-button-section {
  display: flex;
  justify-content: center;

  .exam-button {
    width: 100%;
    max-width: 400px;
    height: 50px;
    background: linear-gradient(135deg, #1890ff, #36cfc9);
    color: white;
    border: none;
    border-radius: 25px;
    font-size: 16px;
    font-weight: 600;
    box-shadow: 0 4px 12px rgba(24, 144, 255, 0.3);

    &:disabled {
      background: #d1d5db;
      cursor: not-allowed;
      box-shadow: none;
    }
  }
  .exam-button-disabled {
    background: #d1d5db;
    cursor: not-allowed;
    box-shadow: none;
  }
}
</style>
